import { useEffect, useLayoutEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

/**
 * Shared rich-text editor wrapping Quill 2.
 *   value     HTML string (controlled)
 *   onChange  called with the editor's HTML on user edits
 *   readOnly  hides the toolbar and disables editing
 *   placeholder / modules / formats / className
 *
 * Uses no findDOMNode and no legacy lifecycles, so it is React 18/19 +
 * StrictMode safe.
 */

const DEFAULT_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote'],
  ],
};

// In Quill 2, ordered + bullet are both the single "list" format, so "bullet"
// is no longer whitelisted separately (the bullet toolbar button still works).
const DEFAULT_FORMATS = [
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'align',
];

export default function QuillEditor({
  value = '',
  onChange,
  readOnly = false,
  placeholder,
  modules,
  formats,
  className,
}) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  // Last HTML we emitted, used to tell our own echo (parent feeding state back
  // in as `value`) apart from a genuine external value change.
  const lastValueRef = useRef(value);

  // Keep the latest onChange without re-initialising the editor.
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });

  // Initialise Quill once on mount.
  useEffect(() => {
    const container = containerRef.current;
    const editor = container.appendChild(document.createElement('div'));

    const quill = new Quill(editor, {
      theme: 'snow',
      readOnly,
      placeholder,
      modules: readOnly ? { toolbar: false } : modules || DEFAULT_MODULES,
      formats: formats || DEFAULT_FORMATS,
    });
    quillRef.current = quill;

    if (value) {
      quill.setContents(quill.clipboard.convert({ html: value }), 'silent');
    }

    quill.on('text-change', (_delta, _oldDelta, source) => {
      // Only react to user input; ignore our own programmatic updates.
      if (source !== 'user') return;
      const html = quill.getSemanticHTML();
      lastValueRef.current = html;
      onChangeRef.current?.(html);
    });

    return () => {
      quillRef.current = null;
      container.innerHTML = '';
    };
    // Intentionally init once: modules/formats/readOnly are fixed per mount in
    // every current usage; readOnly toggles are handled by the effect below.
  }, []);

  // Push external value changes into the editor without clobbering the caret
  // while the user is typing.
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    if (value === lastValueRef.current) return; // our own echo — ignore

    lastValueRef.current = value;
    const selection = quill.getSelection();
    quill.setContents(quill.clipboard.convert({ html: value || '' }), 'silent');
    if (selection) {
      const length = quill.getLength();
      quill.setSelection(Math.min(selection.index, length - 1), 0, 'silent');
    }
  }, [value]);

  // Reflect readOnly toggles.
  useEffect(() => {
    quillRef.current?.enable(!readOnly);
  }, [readOnly]);

  return <div ref={containerRef} className={className} />;
}
